import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import Button from "@mui/material/Button";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Stack, IconButton, Typography, Snackbar, Alert} from "@mui/material";

import "./Category.css";

const EditCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [categoryTitle, setCategoryTitle] = useState("");
  const [originalTitle, setOriginalTitle] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [showSubcategories, setShowSubcategories] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  //state-uri pentru validare si notificari
  const [titleError, setTitleError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "", 
    severity: "success"
  })

  useEffect(() => {
    const fetchCategoryWithSubcategories = async () => {
      try {
        const res = await axiosInstance.get(`/category/${id}/`);

        setCategoryTitle(res.data.title);
        setOriginalTitle(res.data.title);
        setSubcategories(
          res.data.subcategories.map((subcat) => ({
            id: subcat.id,
            title: subcat.title,
            isSaved: true,
            existing: true,
          }))
        );
        setShowSubcategories(true);
      } catch (error) {
        console.error("Error fetching category with subcategories:", error);
        setSnackbar({
          open: true,
          message: "Failed to load category data.",
          severity: "error"
        });
      }
    };

    fetchCategoryWithSubcategories();
  }, [id]);

  //functie pentru verificarea existentei titlului (exclude categoria curenta)
  const checkTitleExists = async (title) => {
    if(!title.trim()){
      return false;
    }
    
    try {
      const response = await axiosInstance.get("/category/check-title/", {
        params: {title: title.trim()}
      });

      return response.data.exists;
    } catch (error) {
      console.error("Error checking title: ", error);
      return false;
    }
  } 

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCategoryTitle(value);

    //reseteaza eroarea la modificare titlului
    if(titleError) {
      setTitleError("");
    }
  };

  const handleSubcategoryChange = (index, value) => {
    const updated = [...subcategories];
    updated[index].title = value;
    setSubcategories(updated);
  };

  const handleAddSubcategory = () => {
    setShowSubcategories(true);
    setSubcategories([...subcategories, { title: "", isSaved: false }]);
  };

  const handleSaveSubcategory = (index) => {
    //verifica daca titlul nu este gol
    if(!subcategories[index].title.trim()) {
      setSnackbar({
        open: true, 
        message: "Subcategory title cannot be empty.",
        severity: "error"
      });
      return;
    }

    //daca titlul este valid, salveaza subcategoria
    const updated = [...subcategories];
    updated[index].isSaved = true;
    setSubcategories(updated);
  };

  const handleEditSubcategory = (index) => {
    const updated = [...subcategories];
    updated[index].isSaved = false;
    setSubcategories(updated);
  };

  const handleRemoveSubcategory = (index) => {
    setSubcategoryToDelete(index);
    setOpenDialog(true);
  };

  const confirmDeleteSubcategory = async () => {
    const index = subcategoryToDelete;
    const subcatToDelete = subcategories[index];

    if (subcatToDelete.existing && subcatToDelete.id) {
      try {
        await axiosInstance.delete(`/subcategory/${subcatToDelete.id}/`);
      } catch (error) {
        console.error("Failed to delete subcategory:", error.response?.data || error.message);
        const msg = error.response?.data?.detail || "An error occurred.";
        setErrorMessage(msg);
        setErrorDialogOpen(true);
        return;
      }
    }

    const updated = [...subcategories];
    updated.splice(index, 1);
    setSubcategories(updated);
    setOpenDialog(false);
    
    setSnackbar({
      open: true,
      message: "Subcategory deleted successfully.",
      severity: "success"
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({...snackbar, open: false});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    //prevenire submit-uri multiple
    if (isSubmitting) return;
    setIsSubmitting(true);

    //verificare titlu gol
    if(!categoryTitle.trim()) {
      setTitleError("Category title is required");
      setSnackbar({
        open: true, 
        message: "Category title is required", 
        severity: "error"
      });
      setIsSubmitting(false);
      return;
    }

    //verifica doar daca titlul s-a schimbat
    if(categoryTitle.trim() !== originalTitle) {
      try {
        const exists = await checkTitleExists(categoryTitle);
        if(exists) {
          setTitleError("A category with this name already exists. Please choose a different title.")
          setSnackbar({
            open: true, 
            message: "A category with this name already exists. Please choose a different title.",
            severity: "error"
          });
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        setSnackbar({
          open: true, 
          message: "Error checking category existence. Please try again.",
          severity: "error"
        });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      //actualizare categorie
      await axiosInstance.put(`/category/${id}/`, { title: categoryTitle });

      //procesare subcategorii
      const subcatRequests = subcategories
        .map((s) => {
          if (s.title.trim() === "") return null;

          if (s.existing) {
            return axiosInstance.put(`/subcategory/${s.id}/`, {
              title: s.title,
              category: id,
            });
          } else {
            return axiosInstance.post("/subcategory/", {
              title: s.title,
              category: id,
            });
          }
        })
        .filter(request => request !== null); //filtreaza request-urile null

      if (subcatRequests.length > 0) {
        await Promise.all(subcatRequests);
      }
      
      //afiseaza mesaj de succes
      setSnackbar({
        open: true,
        message: "Category updated successfully!",
        severity: "success"
      });
      
      //navigheaza inapoi dupa un mic delay
      setTimeout(() => {
        navigate(-1);
      }, 1500);
      
    } catch (error) {
      console.error("Error updating category:", error.response?.data || error.message);

      //verifica daca eroarea este de la titlul duplicat
      if(error.response?.status === 400 && 
         error.response?.data?.title?.includes("already exists")) {
        setTitleError("The name of this category already exists in the database.");
        setSnackbar({
          open: true, 
          message:"A category with this name already exists. Please choose a different title.",
          severity: "error"
        });
      } else {
        setSnackbar({
          open: true, 
          message: "An error occurred. Please try again.",
          severity: "error"
        });
      }
    }
  };

  return (
    <div className="category-page">
    <div className="category-header-container">
      <div className="category-header">
        <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
        <h2 className="add-category-title">Edit category</h2>
      </div>
    </div>

    <hr className="title-separator" />

     <div className="add-category-container">
      <form onSubmit={handleSubmit} className="add-category-form">
        <div className="form-group">
          <label htmlFor="category-title" className="label">
            Title<span className="category-required-star">*</span>
          </label>
          <input
            id="category-title"
            type="text"
            className="input"
            value={categoryTitle}
            onChange={handleCategoryChange}
            required
          />
          {titleError && (
            <Typography
              variant="caption"
              color="error"
              sx={{mt: 0, display: "block"}}
            >
              {titleError}
            </Typography>
          )}
        </div>

        <h4>Subcategories (Types)</h4>
        {showSubcategories && (
          <>
            {subcategories.map((subcat, index) => (
              <div className="subcategory-row" key={index}>
                {!subcat.isSaved ? (
                  <div className="subcategory-flex">
                    <label className="label">
                      Title<span className="category-required-star">*</span>
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={subcat.title}
                      onChange={(e) => handleSubcategoryChange(index, e.target.value)}
                      placeholder={`Subcategory ${index + 1}`}
                    />
                    <div className="subcategory-actions">
                      <button
                        type="button"
                        className="button save-subcat"
                        onClick={() => handleSaveSubcategory(index)}
                      >
                        <span className="icon-desktop">Save</span>
                        <CheckIcon className="icon-mobile" />
                      </button>
                      <button
                        type="button"
                        className="button delete-subcat"
                        onClick={() => handleRemoveSubcategory(index)}
                      >
                        <span className="icon-desktop">Delete</span>
                        <CloseIcon className="icon-mobile" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="subcategory-saved">
                    <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: "4px 8px",
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <FiberManualRecordIcon sx={{ fontSize: 12, color: "gray" }} />
                    <Typography variant="body2" className="subcategory-title">
                      {subcat.title}
                    </Typography>
                    <IconButton size="small" onClick={() => handleEditSubcategory(index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        <Button
            type="button"
            variant="contained"
            className="add-subcat-button"
            onClick={handleAddSubcategory}
            startIcon={<AddIcon />}
          >
          {subcategories.length > 0 ? "Add another subcategory" : "Add subcategories"}
        </Button>

        <div className="save-button-right">
          <button 
            type="submit" 
            className="button-save"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this subcategory?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteSubcategory} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        aria-labelledby="error-dialog-title"
      >
        <DialogTitle id="error-dialog-title">Error</DialogTitle>
        <DialogContent>
          <Typography>{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
        <Button
          onClick={() => {
            setErrorDialogOpen(false);
            setOpenDialog(false);
          }}
          autoFocus
        >
          OK
        </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "success" ? 3000 : 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{vertical: "top", horizontal: "center"}}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
    </div>
  );
};

export default EditCategory;